/**
 * skeleton.ts — where every limb of a duellist is, as capsules.
 *
 * THE point of this module: the capsules listed here are BOTH what the client
 * draws and what the server sweeps. There is one function, both ends call it,
 * and a limb therefore cannot be drawn anywhere other than where it can be hit.
 *
 * That is the same rule the sword already obeys — one transform sequence per
 * tick, read by the renderer and the sweep alike (AGENTS.md: one transform
 * sequence). The body used to be the exception: the client posed ten shaped
 * pills for the look, and the server tested a single fat capsule wrapping the
 * lot. Which meant a hit "on the head" was a hit above 82% of a cylinder, a
 * blade could pass visibly between someone's legs and still connect, and no
 * amount of care with the art could change either.
 *
 * ## What is a limb
 *
 * A capsule between two points in ROOT space — measured from the FEET, the same
 * frame the baked trajectories use, because the two get composed against each
 * other. Nothing here knows about the capsule-centre frame the renderer's group
 * happens to sit in; that offset is the renderer's business.
 *
 * ## Round capsules, oval bodies
 *
 * A person is wider than they are deep and a capsule is not. The radii below
 * take the WIDER axis, so a limb hitbox is a few millimetres proud of the drawn
 * silhouette at the sides rather than a few millimetres inside it. That is the
 * right way to be wrong: a hit that lands just off the visible edge reads as
 * generous, and one that passes through a visible shoulder reads as broken.
 *
 * ## Why the gait is in here
 *
 * Legs are hittable now, so where a leg IS has to be something the server knows
 * — it cannot be a flourish the client adds after the fact. Stride phase is
 * therefore simulation state, advanced from distance travelled by the same
 * function on both ends and carried in the snapshot.
 */
import { FEEL } from './feel.js';
import { shoulderMidFor } from './combat.js';
/**
 * Every capsule a body is made of.
 *
 * Named for the part rather than the bone where the two differ — `chest` and
 * `stomach` are two capsules over one spine, because that is how the trunk is
 * drawn and the whole point is that the two agree.
 */
export const LIMB_IDS = [
    'head',
    'chest',
    'stomach',
    'pelvis',
    'upperArmR',
    'forearmR',
    'upperArmL',
    'forearmL',
    'thighR',
    'shinR',
    'footR',
    'thighL',
    'shinL',
    'footL',
];
/** The radius each limb's capsule is built at. */
export const LIMB_RADIUS = {
    head: FEEL.HEAD_RADIUS,
    chest: FEEL.CHEST_RADIUS,
    stomach: FEEL.WAIST_RADIUS,
    pelvis: FEEL.PELVIS_RADIUS,
    upperArmR: FEEL.ARM_RADIUS,
    forearmR: FEEL.ARM_RADIUS,
    upperArmL: FEEL.ARM_RADIUS,
    forearmL: FEEL.ARM_RADIUS,
    thighR: FEEL.THIGH_RADIUS,
    shinR: FEEL.SHIN_RADIUS,
    footR: FEEL.FOOT_RADIUS,
    thighL: FEEL.THIGH_RADIUS,
    shinL: FEEL.SHIN_RADIUS,
    footL: FEEL.FOOT_RADIUS,
};
/**
 * Which damage zone each limb belongs to.
 *
 * This is what replaced guessing the zone from contact HEIGHT. A hit on the
 * head is now a hit on the head capsule, not a hit above 82% of a cylinder — so
 * ducking, lunging and a leg thrown forward in a stride all change what you can
 * reach, because they move the actual shapes rather than sliding a number up
 * and down a bounding box.
 *
 * Several limbs share a zone: armour is bought per slot, and nobody wants to
 * shop for a left-shin plate. The zone is the unit of protection; the limb is
 * the unit of geometry.
 */
export const LIMB_ZONE = {
    head: 'head',
    chest: 'torso',
    stomach: 'torso',
    pelvis: 'torso',
    upperArmR: 'arm',
    forearmR: 'arm',
    upperArmL: 'arm',
    forearmL: 'arm',
    thighR: 'legs',
    shinR: 'legs',
    thighL: 'legs',
    shinL: 'legs',
    footR: 'feet',
    footL: 'feet',
};
// ---------------------------------------------------------------------------
// gait
// ---------------------------------------------------------------------------
const TWO_PI = Math.PI * 2;
/**
 * Advances a stride phase by a step of ground travel, in the character's frame.
 *
 * Phase comes from DISTANCE COVERED, not from a timer, so cadence is correct at
 * every speed for free: one cycle per `GAIT_CYCLE_M` however fast that metre
 * goes past. A timed cycle has to be re-tuned against speed and skates the
 * moment the two disagree.
 *
 * Backwards travel runs the cycle backwards, so a backpedal does not read as a
 * confident stroll in the wrong direction.
 */
export function advanceStridePhase(phase, dx, dz, yaw) {
    const travelled = Math.hypot(dx, dz);
    const forward = dx * -Math.sin(yaw) + dz * -Math.cos(yaw);
    const direction = forward < 0 ? -1 : 1;
    return (phase + (direction * travelled * TWO_PI) / FEEL.GAIT_CYCLE_M + TWO_PI) % TWO_PI;
}
/** Full stride amplitude is reached at this fraction of top speed. */
const GAIT_FULL_AT = 0.5;
/** How much of a full stride a ground speed is worth, 0..1. */
export const strideFor = (speed) => Math.min(1, Math.max(0, speed / (FEEL.MOVE_SPEED * GAIT_FULL_AT)));
/**
 * Where one foot sits at a phase, in root space: `swing` is fore/aft travel and
 * `lift` is height. `stride` scales it from 0 (planted) to 1 (full walk).
 *
 * `GAIT_STRIDE` is not a free number. At `GAIT_CYCLE_M / 2π` the foot's world
 * speed at mid-stance is exactly zero — it plants, the body passes over it, and
 * it leaves again — which is what stops the walk looking like a shuffle on ice.
 */
export function strideFoot(phase, stride) {
    return {
        // Local forward is -Z, so callers negate: a positive swing is in front.
        swing: Math.sin(phase) * FEEL.GAIT_STRIDE * stride,
        // Lift only through the forward half of the cycle. cos is the derivative of
        // the swing, so it is positive exactly while the foot travels forwards; a
        // foot that rises on the way back is a foot dragged through the floor.
        lift: Math.max(0, Math.cos(phase)) * FEEL.GAIT_LIFT * stride,
    };
}
// ---------------------------------------------------------------------------
// the legs
// ---------------------------------------------------------------------------
/**
 * Solves the knee for a two-bone leg from `hip` down to `ankle`.
 *
 * Unequal bones, so the knee is not the midpoint: it lies where the circles of
 * radius THIGH_BONE and SHIN_BONE meet, pushed onto the FORWARD side of the
 * hip-to-ankle line. Forward always — a knee that solved to whichever side
 * scored best would occasionally bend backwards, and there is no pose in this
 * game where that is anything but a broken leg.
 */
export function solveKnee(hip, ankle) {
    let dx = ankle[0] - hip[0];
    let dy = ankle[1] - hip[1];
    let dz = ankle[2] - hip[2];
    const distance = Math.hypot(dx, dy, dz);
    if (distance < 1e-6)
        return [hip[0], hip[1], hip[2]];
    dx /= distance;
    dy /= distance;
    dz /= distance;
    const { THIGH_BONE, SHIN_BONE } = FEEL;
    // Clamped so the two circles always intersect, whatever the gait asks for.
    const reach = Math.min(Math.max(distance, Math.abs(THIGH_BONE - SHIN_BONE) + 1e-3), THIGH_BONE + SHIN_BONE - 1e-3);
    const along = (reach * reach + THIGH_BONE * THIGH_BONE - SHIN_BONE * SHIN_BONE) / (2 * reach);
    const offset = Math.sqrt(Math.max(0, THIGH_BONE * THIGH_BONE - along * along));
    // The component of forward (-Z) perpendicular to the leg axis.
    const dot = -dz;
    let px = -dx * dot;
    let py = -dy * dot;
    let pz = -1 - dz * dot;
    const plen = Math.hypot(px, py, pz);
    if (plen < 1e-6) {
        px = 0;
        py = 0;
        pz = -1;
    }
    else {
        px /= plen;
        py /= plen;
        pz /= plen;
    }
    return [
        hip[0] + dx * along + px * offset,
        hip[1] + dy * along + py * offset,
        hip[2] + dz * along + pz * offset,
    ];
}
/** How far the trunk carries on above the shoulder line, m. */
const TRUNK_RISE = 0.055;
/** Linear blend between two root-space points. */
const lerp3 = (a, b, t) => [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
];
/**
 * Every capsule of a body, in root space.
 *
 * Deterministic in its inputs and free of any hidden state, which is what lets
 * the server call it while stepping and the client call it while drawing and
 * get the same answer to the millimetre.
 */
export function skeletonFor({ pose, gaitPhase, stride }) {
    const limb = (id, from, to) => ({
        id,
        from,
        to,
        radius: LIMB_RADIUS[id],
    });
    // ---- the trunk ----
    //
    // The shoulder line comes from the chest attitude, never from wherever the
    // arms ended up. Deriving the trunk from the hands is what used to lay the
    // whole figure on its side (AGENTS.md: the body is a few bounded scalars).
    const shoulder = shoulderMidFor(pose.torso);
    const hip = [0, FEEL.HIP_HEIGHT, 0];
    const ribs = lerp3(hip, shoulder, 0.45);
    /*
     * The trunk rises ABOVE the shoulder line.
     *
     * The shoulder line is where the arms are hung, not the top of the body — a
     * person's trapezius carries on up towards the neck, and the deltoids sit
     * beside it rather than on top of it. Ending the chest exactly at the joint
     * left the shoulder balls floating a hand's width proud of a torso that
     * stopped beneath them.
     */
    const chestTop = [shoulder[0], shoulder[1] + TRUNK_RISE, shoulder[2]];
    const limbs = [
        limb('chest', ribs, chestTop),
        limb('stomach', hip, ribs),
        limb('pelvis', [hip[0], hip[1] - 0.06, hip[2]], [hip[0], hip[1] + 0.04, hip[2]]),
        /*
         * The head, from the base of the neck to the crown.
         *
         * It starts one RADIUS above the shoulder line, which puts the bottom of
         * its cap exactly ON that line. That is the whole derivation, and both
         * halves of it matter.
         *
         * Starting ON the shoulders instead buried the head's cap six centimetres
         * into the chest — so an overhead could be scored as a body hit because the
         * chest's cap reached higher, and every shoulder piece was geometrically
         * inside the capsule that scores head hits.
         */
        limb('head', [shoulder[0], shoulder[1] + FEEL.HEAD_RADIUS, shoulder[2]], [
            shoulder[0],
            Math.min(FEEL.BODY_HEIGHT - FEEL.HEAD_RADIUS, shoulder[1] + FEEL.HEAD_RADIUS + 0.16),
            shoulder[2],
        ]),
        limb('upperArmR', pose.arm.shoulder, pose.arm.elbow),
        limb('forearmR', pose.arm.elbow, pose.arm.hand),
        limb('upperArmL', pose.offArm.shoulder, pose.offArm.elbow),
        limb('forearmL', pose.offArm.elbow, pose.offArm.hand),
    ];
    // ---- the legs ----
    for (const side of [-1, 1]) {
        const suffix = side > 0 ? 'R' : 'L';
        // Half a cycle apart, which is what makes it a walk rather than a hop.
        const { swing, lift } = strideFoot(gaitPhase + (side > 0 ? Math.PI : 0), stride);
        const ankle = [
            side * FEEL.STANCE_HALF_WIDTH,
            FEEL.ANKLE_HEIGHT + lift,
            -swing,
        ];
        const hipJoint = [side * 0.085, FEEL.HIP_HEIGHT, 0];
        const knee = solveKnee(hipJoint, ankle);
        limbs.push(limb(`thigh${suffix}`, hipJoint, knee));
        limbs.push(limb(`shin${suffix}`, knee, ankle));
        // The foot runs forward from the ankle, dropping as it lifts, so a raised
        // foot points its toe rather than staying flat like a doll's.
        limbs.push(limb(`foot${suffix}`, ankle, [
            ankle[0],
            Math.max(FEEL.FOOT_RADIUS * 0.5, ankle[1] - lift * 0.5),
            ankle[2] - FEEL.FOOT_LENGTH * 0.75,
        ]));
    }
    return limbs;
}
//# sourceMappingURL=skeleton.js.map